import { useContext, useMemo } from 'react';

import { OptimizationContainer } from '@cast/types';

import { getContainerResourceRecommendations } from 'features/workload-optimization';

import { nonPredictionMemoryMetricKeys, ResourceMetricKey } from './_constants';
import { prepareMemoryChartMetrics } from './_utils';
import { useDisabledMetricsBoxes } from '../../../_hooks/useDisabledMetricsBoxes';
import { useRightsizingTimeRangeSelection } from '../../../_hooks/useRightsizingTimeRangeSelection';
import { WorkloadDetailsContext } from '../../../workload-details/_context';

export const useMemoryChartData = ({
  container,
  timeZone: timeZoneProp,
}: {
  container?: OptimizationContainer;
  timeZone?: string;
}) => {
  const { timeZone: rightsizingTimeZone } = useRightsizingTimeRangeSelection();
  const timeZone = timeZoneProp ?? rightsizingTimeZone;
  const { workload, metrics, isLoading, isFetching, error, refetch } =
    useContext(WorkloadDetailsContext);

  const { toggleMetric, isMetricDisabled } =
    useDisabledMetricsBoxes<ResourceMetricKey>();

  const containerMetrics = useMemo(
    () => metrics?.containers.find(({ name }) => name === container?.name),
    [metrics?.containers, container?.name]
  );

  const resourceRecommendations = useMemo(
    () => (container ? getContainerResourceRecommendations(container) : null),
    [container]
  );

  const chartData = useMemo(() => {
    if (!workload || !container || !resourceRecommendations) {
      return null;
    }

    const { memory } = resourceRecommendations;
    const isReadOnlyMemory = workload?.memory?.isReadOnly;

    return {
      chartData: prepareMemoryChartMetrics(containerMetrics?.memoryGib),
      chartDataKeys: nonPredictionMemoryMetricKeys,
      aggregated: containerMetrics?.memoryGibAggregated,
      requested: memory?.requested,
      recommended: memory?.recommended,
      isReadOnly: isReadOnlyMemory,
    };
  }, [workload, container, containerMetrics, resourceRecommendations]);

  return {
    workload,
    timeZone,
    isLoading,
    isFetching,
    error,
    refetch,
    chartData,
    toggleMetric,
    isMetricDisabled,
  };
};
